import { HelpCircleIcon } from '@approvalmax/ui';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Flex,
    Form,
    Grid,
    Link,
    Popup,
    Progress,
    Select,
    Text,
} from '@approvalmax/ui/src/components';
import { XeroIcon } from 'modules/sprites';
import { memo } from 'react';
import { useForm } from 'react-hook-form';
import { useGetPaymentDetailsRequirements } from 'shared/data/webApp/v2';
import { convertBillingAddressToString } from 'shared/helpers';
import { useCompanyIdBasedOnUrl } from 'shared/hooks';

import { SecondStep } from './components/SecondStep/SecondStep';
import { ThirdStep } from './components/ThirdStep/ThirdStep';
import { countries, currencies, kbLink } from './EditCounterpartyPaymentDetailsPopupContent.constants';
import { useFormSteps, useInitialFormValues } from './EditCounterpartyPaymentDetailsPopupContent.hooks';
import { messages } from './EditCounterpartyPaymentDetailsPopupContent.messages';
import {
    EditCounterpartyPaymentDetailsPopupContentProps,
    FormValues,
} from './EditCounterpartyPaymentDetailsPopupContent.types';

export const EditCounterpartyPaymentDetailsPopupContent = memo<EditCounterpartyPaymentDetailsPopupContentProps>(
    (props) => {
        const { contact, paymentDetailsId } = props;

        const { companyId } = useCompanyIdBasedOnUrl();

        const initialValues = useInitialFormValues({
            contact,
        });

        const form = useForm<FormValues>({
            defaultValues: initialValues,
        });

        const [currencyCode, payerBankCountry, payeeBankCountry] = form.watch([
            'currencyCode',
            'payerBankCountry',
            'payeeBankCountry',
        ]);

        const { data: paymentDetailsRequirements, isFetching: isFetchingGetPaymentDetailsRequirements } =
            useGetPaymentDetailsRequirements({
                path: {
                    companyId,
                },
                query: {
                    currencyCode,
                    payerBankCountry,
                    payeeBankCountry,
                },
            });

        const handleSubmit = form.handleSubmit((values) => {
            // TODO: implement
            console.info({ values });
        });

        const { isSecondStepVisible, isThirdStepVisible } = useFormSteps(form);

        const isLoading = false;

        const noEditChanges = Boolean(paymentDetailsId && !form.formState.isDirty);

        return (
            <Form form={form} onSubmit={handleSubmit}>
                <Popup.Header
                    title={
                        paymentDetailsId
                            ? messages.titleEdit({ contact: contact?.name })
                            : messages.titleNew({ contact: contact?.name })
                    }
                    actions={
                        <Button
                            disabled={noEditChanges}
                            type='submit'
                            color='blue80'
                            size='medium'
                            progress={isLoading}
                        >
                            {messages.save}
                        </Button>
                    }
                />

                <Popup.Body>
                    <Flex direction='column' spacing='24'>
                        <Alert color='midnight20' closable alignItems='center' startIcon={<HelpCircleIcon />}>
                            <Flex direction='column' spacing='4'>
                                <Text font='body' fontSize='small' color='midnight80'>
                                    {messages.helpText}
                                </Text>

                                <Link external font='label' fontSize='small' href={kbLink}>
                                    {messages.learnMore}
                                </Link>
                            </Flex>
                        </Alert>

                        {contact && (
                            <Box color='midnight30' spacing='8' radius='small'>
                                <Flex alignItems='center' spacing='8'>
                                    <XeroIcon />

                                    <Flex direction='column' spacing='4'>
                                        <Text font='label' fontSize='medium' fontWeight='medium'>
                                            {contact.name}
                                        </Text>

                                        {contact.billingAddress && (
                                            <Text font='label' fontSize='small' fontWeight='regular' color='midnight70'>
                                                {convertBillingAddressToString(contact.billingAddress, ', ')}
                                            </Text>
                                        )}
                                    </Flex>
                                </Flex>
                            </Box>
                        )}

                        <Checkbox name='isDefault' disabled={isLoading}>
                            {messages.defaultCheckbox}
                        </Checkbox>

                        <Grid rowGap={32}>
                            <Grid gridTemplateColumns='repeat(2, 1fr)' gap={16}>
                                <Select
                                    name='payeeBankCountry'
                                    label={messages.payeeBankCountry}
                                    placeholder={messages.selectPlaceholder}
                                    items={countries}
                                    required
                                    clearable={false}
                                    autocomplete
                                />

                                <Select
                                    name='currencyCode'
                                    label={messages.accountCurrency}
                                    placeholder={messages.selectPlaceholder}
                                    items={currencies}
                                    required
                                    clearable={false}
                                    autocomplete
                                />

                                <Select
                                    name='payerBankCountry'
                                    label={messages.payerBankCountry}
                                    placeholder={messages.selectPlaceholder}
                                    items={countries}
                                    required
                                    clearable={false}
                                    autocomplete
                                />
                            </Grid>

                            {isFetchingGetPaymentDetailsRequirements && (
                                <Flex alignItems='center' justifyContent='center' height={60}>
                                    <Progress shape='circle' size='small' />
                                </Flex>
                            )}

                            {isSecondStepVisible && !isFetchingGetPaymentDetailsRequirements && (
                                <SecondStep requirements={paymentDetailsRequirements} />
                            )}

                            {isThirdStepVisible && !isFetchingGetPaymentDetailsRequirements && (
                                <ThirdStep requirements={paymentDetailsRequirements} />
                            )}
                        </Grid>
                    </Flex>
                </Popup.Body>
            </Form>
        );
    }
);

EditCounterpartyPaymentDetailsPopupContent.displayName = 'EditCounterpartyPaymentDetailsPopupContent';
